/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import createMethodDecorator from 'apmui/util/createMethodDecorator';

/**
 * The @throttle decorator limits invocation of the method
 * at most once per every 'wait' milliseconds.
 *
 * The decorated method will invoke original method with the last arguments provided
 * and return the result of the last invocation (initial calls return null).
 *
 * @example
 * ```
 *
 * class SomeClass {
 *      constructor() {
 *          document.addEventListener('keyup', this.onKeyUp.bind(this));
 *      }
 *
 *      @throttle(300)
 *      onKeyUp() {
 *         // ...
 *      }
 * }
 * ```
 */
export default function throttle(wait: number) {
    return createMethodDecorator('throttle', (_, fn) => {
        let timer: number | null = null;
        let lastResult: any = null;
        let lastArguments: any = null;

        // use non arrow function for correct 'this' and to avoid copying arguments
        /* eslint-disable prefer-rest-params */
        return function(this: any) {
            lastArguments = arguments;
            if (!timer) {
                timer = window.setTimeout(() => {
                    timer = null;
                    lastResult = fn.apply(this, lastArguments);
                }, wait);
            }
            return lastResult;
        };
    });
}
